import type { ReasonOption, Verdict } from '../types';

// Verdict reason libraries (HUB-504) — ported verbatim from the prototype. Each
// reviewable field draws from a field-specific library plus the shared library
// (`reasonsFor`). Used in the case-review slice; defined here so the domain data
// lands as one foundation.

export const R_SHARED: ReasonOption[] = [
  { id: 'acceptable_alt', label: 'Acceptable alternative – no true error' },
  { id: 'human_error', label: 'Human coder error (agent was correct)' },
  { id: 'agent_extraction_error', label: 'Agent extraction error (misread the record)' },
  { id: 'doc_insufficient', label: 'Documentation insufficient or conflicting' },
  { id: 'doc_unreadable', label: 'Document unreadable / unintelligible' },
  { id: 'missing_anes_record', label: 'Missing Anesthesia / intra-op record' },
  { id: 'missing_signature', label: 'Missing provider signature in the Anesthesia record' },
  { id: 'other', label: 'Other' },
];

export const R_PROC: ReasonOption[] = [
  { id: 'proc_detail_off', label: 'Wrong code – detail off (subcode/approach/laterality/technique)' },
  { id: 'proc_misidentified', label: 'Wrong code – misidentified procedure' },
  { id: 'proc_wrong_primary', label: 'Wrong primary on multi-procedure case' },
  { id: 'proc_bundling', label: 'Bundling / NCCI error' },
  { id: 'proc_dx_unsupported', label: "Dx doesn't support the code (medical necessity / coverage)" },
  { id: 'proc_gender_age', label: 'Wrong code – gender- and age-based procedure' },
  { id: 'proc_missed_extensive', label: 'Wrong code – missed extensive procedure from op report' },
  { id: 'proc_type_af', label: 'Wrong code – missed correct type of procedure (A-type / F-type)' },
];

export const R_ASA: ReasonOption[] = [
  { id: 'asa_wrong_crosswalk', label: 'Wrong crosswalk for the surgical code' },
  { id: 'asa_incorrect_surgical_cpt', label: 'Error due to incorrect surgical CPT' },
  { id: 'asa_anes_primary', label: 'Anesthesia-primary mis-coded as surgical (e.g. OB)' },
  { id: 'asa_wrong_tier', label: 'Wrong tier / base units' },
  { id: 'asa_time_units', label: 'Time or units error' },
];

export const R_ICD: ReasonOption[] = [
  { id: 'icd_wrong', label: 'Wrong diagnosis (clinically incorrect)' },
  { id: 'icd_history_vs_active', label: 'History vs. active condition (Z-code vs. disease)' },
  { id: 'icd_specificity', label: 'Insufficient specificity' },
  { id: 'icd_laterality', label: 'Wrong laterality' },
  { id: 'icd_missing_support', label: 'Missing supporting diagnosis' },
  { id: 'icd_sequencing', label: 'Wrong sequencing / primary dx' },
  { id: 'icd_coding_guideline', label: 'Wrong code – coding guideline (U/V/X/Y/W series coded as primary dx)' },
  { id: 'icd_duplicate', label: 'Wrong code – duplicate codes' },
];

export const R_MOD: ReasonOption[] = [
  { id: 'mod_missing', label: 'Missing required modifier' },
  { id: 'mod_incorrect', label: 'Incorrect modifier' },
  { id: 'mod_wrong_direction', label: 'Wrong provider/direction modifier (AA/QZ/QX/QK/QY/GC)' },
  { id: 'mod_unsupported', label: 'Not supported by documentation' },
];

export const R_PHYS: ReasonOption[] = [
  { id: 'phys_wrong_level', label: 'Wrong physical status level (P1–P6)' },
  { id: 'phys_unsupported', label: 'Not supported by documentation' },
];

export const R_MISSING_DOC: ReasonOption = { id: 'missing_documented', label: 'Missing code supported by documentation' };

export const R_ICD_ADDL: ReasonOption[] = [
  ...R_ICD,
  R_MISSING_DOC,
  { id: 'icd_exclude1', label: 'Wrong code – Excludes1 conflict' },
  { id: 'icd_combo', label: 'Wrong code – coded as 2 separate codes instead of single combo code' },
];

export const R_PROC_ANC: ReasonOption[] = [
  ...R_PROC,
  R_MISSING_DOC,
  { id: 'anc_missing_nerve_block', label: 'Missing internal code for nerve block' },
  { id: 'anc_a_instead_f', label: 'Captured as A-type instead of F-type' },
  { id: 'anc_non_billable', label: 'Non-billable scenario (e.g. block is mode of anesthesia, non-billable provider)' },
];

export const VERDICTS: Verdict[] = [
  { id: 'both', label: 'Both correct', short: 'Both', tone: 'green' },
  { id: 'a', label: 'Result A only', short: 'A only', tone: 'blue' },
  { id: 'b', label: 'Result B only', short: 'B only', tone: 'purple' },
  { id: 'neither', label: 'Neither', short: 'Neither', tone: 'red' },
];

/** Resolve a reason id to its human label across every library. */
export function reasonLabel(id: string): string {
  const all = [...R_SHARED, ...R_PROC_ANC, ...R_ASA, ...R_ICD_ADDL, ...R_MOD, ...R_PHYS];
  const m = all.find((r) => r.id === id);
  return m ? m.label : id;
}
